import type { XmaxTelemetryParams } from './collector'

const DEFAULT_WINDOW_MS = 10_000
const CUMULATIVE_SAMPLE_LIMIT = 14_400
const PERCENTILE_MIN_SAMPLES = 10

export const RTC_METRIC_NAMES = [
  'up_rtt_ms',
  'down_rtt_ms',
  'jitter_ms',
  'up_loss_rate',
  'down_loss_rate',
  'up_bitrate_kbps',
  'down_bitrate_kbps',
  'up_fps',
  'down_decode_fps',
  'down_render_fps',
  'down_receive_fps',
] as const

export type RtcMetricName = typeof RTC_METRIC_NAMES[number]

type TimedValue = { at: number; value: number }

class MetricSeries {
  private readonly window: TimedValue[] = []
  private readonly percentileSamples = new Float32Array(CUMULATIVE_SAMPLE_LIMIT)
  private retainedSamples = 0
  private count = 0
  private sum = 0
  private min = Number.POSITIVE_INFINITY
  private max = Number.NEGATIVE_INFINITY

  constructor(private windowMs: number) {}

  setWindowDuration(ms: number): void { this.windowMs = ms }

  private prune(at: number): void {
    while (this.window[0] && at - this.window[0].at > this.windowMs) this.window.shift()
  }

  add(at: number, value: number): void {
    this.count++
    this.sum += value
    this.min = Math.min(this.min, value)
    this.max = Math.max(this.max, value)
    this.window.push({ at, value })
    this.prune(at)

    if (this.retainedSamples < CUMULATIVE_SAMPLE_LIMIT) {
      this.percentileSamples[this.retainedSamples++] = value
      return
    }
    const replace = Math.floor(Math.random() * this.count)
    if (replace < CUMULATIVE_SAMPLE_LIMIT) this.percentileSamples[replace] = value
  }

  snapshot(prefix: 'window' | 'cumulative', at: number): XmaxTelemetryParams {
    this.prune(at)
    const values = prefix === 'window'
      ? this.window.map(sample => sample.value)
      : this.percentileSamples.subarray(0, this.retainedSamples)
    const count = prefix === 'window' ? values.length : this.count
    if (!count) return { [`${prefix}_sample_count`]: 0 }
    const sorted = Float32Array.from(values).sort()
    const percentile = (ratio: number) => sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * ratio))]
    const result: XmaxTelemetryParams = {
      [`${prefix}_sample_count`]: count,
      [`${prefix}_avg`]: prefix === 'window'
        ? this.window.reduce((total, sample) => total + sample.value, 0) / values.length
        : this.sum / this.count,
      [`${prefix}_min`]: prefix === 'window' ? sorted[0] : this.min,
      [`${prefix}_max`]: prefix === 'window' ? sorted[sorted.length - 1] : this.max,
      [`${prefix}_p50`]: null,
      [`${prefix}_p90`]: null,
    }
    if (count >= PERCENTILE_MIN_SAMPLES) {
      result[`${prefix}_p50`] = percentile(0.5)
      result[`${prefix}_p90`] = percentile(0.9)
    }
    return result
  }
}

export class RtcStatsAccumulator {
  private readonly metrics = new Map<RtcMetricName, MetricSeries>()
  private firstAt?: number
  private lastAt?: number
  private stallCount = 0
  private stallDurationMs = 0

  constructor(private windowMs = DEFAULT_WINDOW_MS) {}

  clear(): void { this.metrics.clear() }

  setWindowDuration(ms: number): void {
    this.windowMs = ms
    this.metrics.forEach(series => series.setWindowDuration(ms))
  }

  add(at: number, values: Partial<Record<RtcMetricName, number>>, stalls?: { count?: number; durationMs?: number }): void {
    this.firstAt ??= at
    this.lastAt = at
    for (const metric of RTC_METRIC_NAMES) {
      const value = values[metric]
      if (typeof value !== 'number' || !Number.isFinite(value)) continue
      let series = this.metrics.get(metric)
      if (!series) { series = new MetricSeries(this.windowMs); this.metrics.set(metric, series) }
      series.add(at, value)
    }
    if (typeof stalls?.count === 'number' && Number.isFinite(stalls.count)) this.stallCount = Math.max(this.stallCount, stalls.count)
    if (typeof stalls?.durationMs === 'number' && Number.isFinite(stalls.durationMs)) this.stallDurationMs = Math.max(this.stallDurationMs, stalls.durationMs)
  }

  snapshot(at = this.lastAt ?? 0): XmaxTelemetryParams | null {
    if (this.firstAt === undefined || this.lastAt === undefined) return null
    const result: XmaxTelemetryParams = {
      window_duration_ms: this.windowMs,
      stats_started_offset_ms: Math.round(this.firstAt),
      stats_last_offset_ms: Math.round(this.lastAt),
      down_stall_count: this.stallCount,
      down_stall_duration_ms: Math.round(this.stallDurationMs),
    }
    this.metrics.forEach((series, metric) => {
      const windowSnapshot = series.snapshot('window', at)
      Object.keys(windowSnapshot).forEach(key => {
        result[key.replace('window', `window_${metric}`)] = windowSnapshot[key]
      })
      const cumulativeSnapshot = series.snapshot('cumulative', at)
      Object.keys(cumulativeSnapshot).forEach(key => {
        result[key.replace('cumulative', `cumulative_${metric}`)] = cumulativeSnapshot[key]
      })
    })
    return result
  }
}
